// src/pages/editor-tasks/EditorTasksPool.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function EditorTasksPool() {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    client: 'all',
    editor: 'all',
    designer: 'all'
  });
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  // Colors
  const colors = {
    backgroundDark: '#0f0c29',
    backgroundMedium: '#302b63',
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444'
  };

  useEffect(() => {
    fetchTasksPool();
    fetchDesigners();
    fetchEditors();
    fetchClients();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, tasks]);

  const fetchTasksPool = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get_editor_tasks_pool.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.tasks)) {
        setTasks(data.tasks);
        setFilteredTasks(data.tasks);
      } else if (Array.isArray(data)) {
        setTasks(data);
        setFilteredTasks(data);
      } else {
        setTasks([]);
        setFilteredTasks([]);
      }
    } catch (error) {
      console.error('Error fetching tasks pool:', error);
      setMessage(`❌ Error loading tasks: ${error.message}`);
      setTasks([]);
      setFilteredTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesigners = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_designers.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.designers)) {
        setDesigners(data.designers);
      } else if (Array.isArray(data)) {
        setDesigners(data);
      } else {
        setDesigners([]);
      }
    } catch (error) {
      console.error('Error fetching designers:', error);
      setDesigners([]);
    }
  };

  const fetchEditors = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_editors.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setEditors(data);
      } else if (data.data && Array.isArray(data.data)) {
        setEditors(data.data);
      } else {
        setEditors([]);
      }
    } catch (error) {
      console.error('Error fetching editors:', error);
      setEditors([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_clients.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setClients(data);
      } else if (data.data && Array.isArray(data.data)) {
        setClients(data.data);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    if (filters.status !== 'all') {
      filtered = filtered.filter(task => task.status === filters.status);
    }

    if (filters.client !== 'all') {
      filtered = filtered.filter(task => task.client_id == filters.client);
    }

    if (filters.editor !== 'all') {
      filtered = filtered.filter(task => task.editor_id == filters.editor);
    }

    if (filters.designer !== 'all') {
      if (filters.designer === 'unassigned') {
        filtered = filtered.filter(task => !task.designer_id || task.designer_id === '');
      } else {
        filtered = filtered.filter(task => task.designer_id == filters.designer);
      }
    }

    setFilteredTasks(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const assignDesigner = async (taskId, designerId) => {
    if (!designerId) {
      setMessage('❌ Please select a designer');
      return;
    }

    try {
      setAssigning(true);
      setMessage('');

      const userId = sessionStorage.getItem('userId');
      const userName = sessionStorage.getItem('userName');

      const response = await fetch(`${API_BASE}/assign_designer_to_editor_task.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: taskId,
          designer_id: designerId,
          assigned_by: userId,
          assigned_by_name: userName
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('✅ Designer assigned successfully!');
        fetchTasksPool(); // Refresh tasks
      } else {
        setMessage(`❌ ${data.message || 'Error assigning designer'}`);
      }
    } catch (error) {
      console.error('Error assigning designer:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setAssigning(false);
    }
  };

  const handleEditClick = (taskId) => {
    navigate(`/edit-editor-task/${taskId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#F59E0B', text: 'Pending', icon: 'fa-clock' },
      assigned: { bg: '#6366F1', text: 'Assigned', icon: 'fa-user-check' },
      in_progress: { bg: '#06B6D4', text: 'In Progress', icon: 'fa-spinner' },
      completed: { bg: '#10B981', text: 'Completed', icon: 'fa-check' },
      'in review': { bg: '#8B5CF6', text: 'In Review', icon: 'fa-eye' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: '600'
      }}>
        <i className={`fas ${config.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {config.text}
      </span>
    );
  };

  const getVideoTypeBadge = (videoType) => {
    const types = {
      'indoor_reel': { color: '#6366F1', label: 'Indoor Reel', icon: 'fa-home' },
      'outdoor_reel': { color: '#10B981', label: 'Outdoor Reel', icon: 'fa-tree' },
      'podcast': { color: '#8B5CF6', label: 'Podcast', icon: 'fa-podcast' },
      'indoor_long_video': { color: '#F59E0B', label: 'Indoor Long', icon: 'fa-video' },
      'outdoor_long_video': { color: '#06B6D4', label: 'Outdoor Long', icon: 'fa-mountain' }
    };
    
    const type = types[videoType] || { color: '#6B7280', label: 'Unknown', icon: 'fa-question' };
    
    return (
      <span style={{
        background: type.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: '600'
      }}>
        <i className={`fas ${type.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {type.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: colors.textLight,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${colors.primary}`,
              borderTop: `2px solid transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading editor tasks pool...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`
        }}>
          <h1 style={{
            color: 'white',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <i className="fas fa-users"></i>
            Editor Tasks Pool
          </h1>
          <p style={{ color: colors.textLight }}>
            Manage all editor tasks, assign designers, and track progress
          </p>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '15px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={fetchTasksPool}
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-sync"></i>
              Refresh
            </button>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-layer-group" style={{ color: colors.primary }}></i>
              <span style={{ color: colors.textLight }}>
                Total: <strong style={{ color: 'white' }}>{tasks.length}</strong>
              </span>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-filter" style={{ color: colors.secondary }}></i>
              <span style={{ color: colors.textLight }}>
                Filtered: <strong style={{ color: 'white' }}>{filteredTasks.length}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            borderRadius: '10px',
            background: message.includes('✅') 
              ? `rgba(16, 185, 129, 0.2)` 
              : `rgba(239, 68, 68, 0.2)`,
            border: `1px solid ${message.includes('✅') ? colors.success : colors.danger}`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
                 style={{ color: message.includes('✅') ? colors.success : colors.danger }}></i>
              <span>{message}</span>
            </div>
            <button
              onClick={() => setMessage('')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textLight,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Filters */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`,
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: 'white', fontSize: '1rem', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-filter"></i>
            Filter Tasks
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            <div>
              <label style={{ display: 'block', color: colors.textLight, fontSize: '0.85rem', marginBottom: '5px' }}>
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${colors.borderDark}`,
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="in review">In Review</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', color: colors.textLight, fontSize: '0.85rem', marginBottom: '5px' }}>
                Client
              </label>
              <select
                name="client"
                value={filters.client}
                onChange={handleFilterChange}
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${colors.borderDark}`,
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}
              >
                <option value="all">All Clients</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', color: colors.textLight, fontSize: '0.85rem', marginBottom: '5px' }}>
                Editor
              </label>
              <select
                name="editor"
                value={filters.editor}
                onChange={handleFilterChange}
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${colors.borderDark}`,
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}
              >
                <option value="all">All Editors</option>
                <option value="unassigned">Unassigned</option>
                {editors.map(editor => (
                  <option key={editor.id} value={editor.id}>
                    {editor.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', color: colors.textLight, fontSize: '0.85rem', marginBottom: '5px' }}>
                Designer
              </label>
              <select
                name="designer"
                value={filters.designer}
                onChange={handleFilterChange}
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${colors.borderDark}`,
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}
              >
                <option value="all">All Designers</option>
                <option value="unassigned">Unassigned</option>
                {designers.map(designer => (
                  <option key={designer.id} value={designer.id}>
                    {designer.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tasks Table */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`,
          overflow: 'hidden'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            padding: '15px 20px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-tasks"></i>
              Editor Tasks Pool
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'white',
              fontSize: '0.85rem'
            }}>
              <thead>
                <tr style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: `2px solid ${colors.borderDark}`
                }}>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Video Name</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Client</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Editor</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Designer</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Count</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ padding: '40px', textAlign: 'center', color: colors.textLight }}>
                      <i className="fas fa-search fa-2x" style={{ marginBottom: '15px', opacity: 0.5 }}></i>
                      <div>No tasks found matching your filters</div>
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <tr key={task.id} style={{
                      borderBottom: `1px solid ${colors.borderDark}`,
                      transition: 'background 0.3s'
                    }}>
                      <td style={{ padding: '12px 15px', fontWeight: '600' }}>
                        #{task.id}
                      </td>
                      <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <span>{task.video_name}</span>
                          {task.raw_link && (
                            <a
                              href={task.raw_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: colors.primary,
                                fontSize: '0.75rem',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="fas fa-external-link-alt"></i>
                              View Link
                            </a>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getVideoTypeBadge(task.video_type)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.client_name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getStatusBadge(task.status)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.editor_name || (
                          <span style={{ color: colors.warning, fontSize: '0.8rem' }}>
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.designer_name || (
                          <span style={{ color: colors.warning, fontSize: '0.8rem' }}>
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px', color: colors.textLight }}>
                        {formatDate(task.created_at)}
                      </td>
                      <td style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>
                        {task.video_count || 1}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditClick(task.id)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.warning}, #eab308)`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            title="Edit Task"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          
                          <select
                            value={task.designer_id || ''}
                            onChange={(e) => assignDesigner(task.id, e.target.value)}
                            disabled={assigning}
                            style={{
                              background: task.designer_id ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                              border: `1px solid ${task.designer_id ? colors.success : colors.borderDark}`,
                              color: 'white',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              minWidth: '120px'
                            }}
                          >
                            <option value="">Assign Designer</option>
                            {designers.map(designer => (
                              <option key={designer.id} value={designer.id}>
                                {designer.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        tr:hover {
          background: rgba(255, 255, 255, 0.03) !important;
        }
        
        select:focus {
          outline: none;
          border-color: ${colors.primary} !important;
        }
        
        select option {
          background: ${colors.backgroundDark};
          color: white;
        }
      `}</style>
    </Layout>
  );
}

export default EditorTasksPool;